import { TypedDocumentNode, gql } from '@apollo/client';
import { faPen } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useEffect, useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  defineMessages,
  useIntl,
} from 'react-intl';
import { Link, generatePath } from 'react-router-dom';
import styled from 'styled-components';

import defaultShield from '@sorare/core/src/assets/club/shield_none.png';
import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Scarcities } from '@sorare/core/src/atoms/icons/Scarcities';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Text14, Text16, Title2 } from '@sorare/core/src/atoms/typography';
import { SportLogo } from '@sorare/core/src/components/SportLogo';
import { GraphQLResult } from '@sorare/core/src/components/form/Form';
import ActiveUserAvatar from '@sorare/core/src/components/user/ActiveUserAvatar';
import DiscordUser from '@sorare/core/src/components/user/DiscordUser';
import FollowButton from '@sorare/core/src/components/user/FollowButton';
import TwitterUser from '@sorare/core/src/components/user/TwitterUser';
import UserName from '@sorare/core/src/components/user/UserName';
import { NETWORK_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { TradeButton } from '@sorare/marketplace/src/components/TradeButton';

import { cardCountsToRarities } from '../utils';
import { ClubEditor } from './ClubEditor';
import { Share } from './Share';
import {
  Header_cardCountsByRarity,
  Header_socialPictureDerivative,
  Header_user,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 5 })`
  --gradient: linear-gradient(
    180deg,
    rgba(var(--c-rgb-neutral-200), 0.2) 0%,
    rgba(var(--c-rgb-neutral-200), 1) 100%
  );
  padding: var(--double-unit);
  background-position: top center;
  background-size:
    100% 50%,
    auto 50%;
  background-repeat: no-repeat;
  background-color: var(--c-neutral-200);
  border-radius: var(--double-unit);

  @media ${tabletAndAbove} {
    gap: 0;
  }
`;
const Group = styled(Horizontal)`
  &.smallGap {
    gap: var(--half-unit);
  }
`;
const Content = styled(Vertical).attrs({ gap: 2, center: true })`
  justify-content: stretch;
`;
const UsernameGroup = styled(Group)`
  overflow: hidden;
`;
const Username = styled(Text16)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Club = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: stretch;
`;
const ClubShield = styled.img`
  width: calc(12 * var(--unit));
`;
const Details = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: stretch;
`;
const Ctas = styled(Horizontal).attrs({ center: true })`
  flex-wrap: wrap;
`;

export type Props = {
  user: Header_user;
  clubName?: string | null;
  onSubmit: (data: { clubName: string }) => Promise<GraphQLResult | null>;
  bannerUrl?: string | null;
  shieldUrl?: string | null;
  cardCount: Header_cardCountsByRarity;
  clubEdition?: ReactNode;
  managerProfilePath: string;
  managerProfileSocialPictures?: Header_socialPictureDerivative;
  managerProfileSocialPictureFingerprint?: string;
  galleryPath: string;
  gallerySocialPictures?: Header_socialPictureDerivative;
  highlightedDeckPath?: string;
  highlightedDeckSocialPictures?: Header_socialPictureDerivative;
};

export const messages = defineMessages({
  joined: {
    id: 'ManagerProfile.Header.joined',
    defaultMessage: 'Joined',
  },
});

export const Header = ({
  user,
  clubName,
  onSubmit,
  bannerUrl,
  shieldUrl,
  cardCount,
  clubEdition,
  managerProfilePath,
  managerProfileSocialPictures,
  managerProfileSocialPictureFingerprint,
  galleryPath,
  gallerySocialPictures,
  highlightedDeckPath,
  highlightedDeckSocialPictures,
}: Props) => {
  const { sport } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const [openUpdateProfileDialog, setOpenUpdateProfileDialog] = useState(false);
  const { setPageMetadata } = useSeoContext();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (managerProfileSocialPictures) {
      return setPageMetadata(
        clubName ||
          formatMessage(navLabels.userClub, {
            username: user.nickname,
          }),
        {
          img: managerProfileSocialPictures?.post,
        }
      );
    }
    return () => {};
  }, [
    user.nickname,
    clubName,
    managerProfileSocialPictures,
    setPageMetadata,
    formatMessage,
  ]);

  if (!sport) return null;

  const isMe = currentUser && currentUser.slug === user.slug;

  const totalCards =
    cardCount.limited +
    cardCount.rare +
    cardCount.super_rare +
    cardCount.unique;

  return (
    <Root
      style={
        bannerUrl
          ? {
              backgroundImage: `var(--gradient), url(${bannerUrl})`,
            }
          : {}
      }
    >
      <SBHorizontal gap={0} className="spaceBetween">
        <UsernameGroup>
          <ActiveUserAvatar user={user} variant="medium" />
          <Username as="h1" color="var(--c-static-neutral-100)">
            <UserName user={user} />
          </Username>
        </UsernameGroup>
        <Group>
          <SportLogo sport={sport} />
        </Group>
      </SBHorizontal>
      <Content>
        <Club>
          <>
            <ClubShield src={shieldUrl || defaultShield} alt={clubName || ''} />
            <Title2 color="var(--c-static-neutral-100)" className="text-center">
              {clubName}
            </Title2>
          </>
          {!!totalCards && (
            <Link to={galleryPath}>
              <Group>
                <Text14 color="var(--c-static-neutral-100)">
                  <FormattedNumber value={totalCards} />
                </Text14>
                <Scarcities rarities={cardCountsToRarities(cardCount)} />
              </Group>
            </Link>
          )}
        </Club>
        {user.profile.status && (
          <Text16 color="var(--c-static-neutral-100)" className="text-center">
            {user.profile.status}
          </Text16>
        )}
        <Details>
          <Group>
            {user.profile.twitterUsername && (
              <Text14 color="var(--c-static-neutral-100)" as="div">
                <TwitterUser userProfile={user.profile} />
              </Text14>
            )}
            {user.profile.discordUsername && (
              <Text14 color="var(--c-static-neutral-100)" as="div">
                <DiscordUser userProfile={user.profile} />
              </Text14>
            )}
          </Group>
          <Group>
            <Link
              to={`${generatePath(NETWORK_SLUG, {
                slug: user.slug,
              })}?tab=followers`}
            >
              <Group className="smallGap">
                <Text14 color="var(--c-static-neutral-100)">
                  <FormattedNumber
                    value={user.followersCount}
                    compactDisplay="short"
                    notation="compact"
                    maximumFractionDigits={1}
                  />
                </Text14>
                <Text14 color="var(--c-static-neutral-500)">
                  <FormattedMessage {...glossary.followers} />
                </Text14>
              </Group>
            </Link>
            <Link
              to={`${generatePath(NETWORK_SLUG, {
                slug: user.slug,
              })}?tab=following`}
            >
              <Group className="smallGap">
                <Text14 color="var(--c-static-neutral-100)">
                  <FormattedNumber
                    value={user.followingCount}
                    compactDisplay="short"
                    notation="compact"
                    maximumFractionDigits={1}
                  />
                </Text14>
                <Text14 color="var(--c-static-neutral-500)">
                  <FormattedMessage {...glossary.following} />
                </Text14>
              </Group>
            </Link>
            <Group className="smallGap">
              <Text14 color="var(--c-static-neutral-100)">
                <FormattedDate
                  value={user.createdAt}
                  month="2-digit"
                  day="2-digit"
                  year="numeric"
                />
              </Text14>
              <Text14 color="var(--c-static-neutral-500)">
                <FormattedMessage {...messages.joined} />
              </Text14>
            </Group>
          </Group>
        </Details>
        <Ctas>
          {isMe ? (
            <>
              <Share
                managerProfilePath={managerProfilePath}
                managerProfileSocialPictures={managerProfileSocialPictures}
                galleryPath={galleryPath}
                gallerySocialPictures={gallerySocialPictures}
                highlightedDeckPath={highlightedDeckPath}
                highlightedDeckSocialPictures={highlightedDeckSocialPictures}
                fingerprint={managerProfileSocialPictureFingerprint}
              />
              <Button
                size="medium"
                color="tertiary"
                onClick={() => setOpenUpdateProfileDialog(true)}
              >
                <FontAwesomeIcon icon={faPen} />
                <span>
                  <FormattedMessage {...glossary.edit} />
                </span>
              </Button>
              <ClubEditor
                open={openUpdateProfileDialog}
                onClose={() => setOpenUpdateProfileDialog(false)}
                onSubmit={async data => {
                  if (data.clubName) {
                    return onSubmit({ clubName: data.clubName });
                  }
                  return null;
                }}
                clubEdition={clubEdition}
                clubName={clubName || ''}
              />
            </>
          ) : (
            <>
              <TradeButton user={user} />
              <FollowButton
                size="medium"
                withIcon
                subscribable={{ __typename: 'User', slug: user.slug }}
                initialSubscription={user.followed}
              />
            </>
          )}
        </Ctas>
      </Content>
    </Root>
  );
};

Header.fragments = {
  user: gql`
    fragment Header_user on User {
      id
      slug
      nickname
      createdAt
      followersCount
      followingCount
      followed {
        slug
      }
      profile {
        id
        status
        discordUsername
        twitterUsername
      }
      ...ActiveUserAvatar_user
      ...UserName_publicUserInfoInterface
      ...TradeButton_publicUserInfoInterface
    }
    ${ActiveUserAvatar.fragments.user}
    ${UserName.fragments.user}
    ${TradeButton.fragments.user}
  ` as TypedDocumentNode<Header_user>,
  socialPictureDerivative: gql`
    fragment Header_socialPictureDerivative on SocialPictureDerivative {
      ...Share_socialPictureDerivative
    }
    ${Share.fragments.socialPictureDerivative}
  ` as TypedDocumentNode<Header_socialPictureDerivative>,
  cardCountsByRarity: gql`
    fragment Header_cardCountsByRarity on CardCountsByRarity {
      unique: uniqueCount
      super_rare: superRareCount
      rare: rareCount
      limited: limitedCount
    }
  ` as TypedDocumentNode<Header_cardCountsByRarity>,
};
